/**
 * Shown under the contact picker in a classification path once an EXISTING
 * contact is chosen. Two affordances, both operating on the shared contact
 * record (so a fix lands everywhere that contact appears, past and future):
 *
 *   - "View contact card" — a plain-anchor link (new tab) to the contact
 *     detail page, the escape hatch for editing name / employer / phone.
 *   - Inline address editor — surfaces only when filing actually needs it
 *     (row is over the itemization threshold AND the contact's mailing
 *     address is incomplete). It fills a gap the picker otherwise strands:
 *     you can set an address when CREATING a contact, but not when reusing an
 *     existing one. Never blocks Classify — alert, don't block.
 */
import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { FinanceContactDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';
// Direct import (not the barrel) to avoid a cycle — the barrel also re-exports
// this editor.
import { AddressAutocomplete } from './AddressAutocomplete';

export interface ContactAddressPatch {
  address: string;
  /** Optional second postal line (suite/apt). Empty string when unset. */
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

interface SelectedContactAddressEditorProps {
  contact: FinanceContactDto;
  /** The word for this contact in this path — "payee" or "donor". */
  noun: string;
  /** True when the row is at/above the itemization threshold (address matters). */
  addressMattersForFiling: boolean;
  isSaving: boolean;
  onSaveAddress: (patch: ContactAddressPatch) => void | Promise<void>;
}

function addressIsIncomplete(c: FinanceContactDto): boolean {
  return !c.address || !c.city || !c.state || !c.zipCode;
}

export function SelectedContactAddressEditor({
  contact,
  noun,
  addressMattersForFiling,
  isSaving,
  onSaveAddress,
}: SelectedContactAddressEditorProps) {
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<ContactAddressPatch>({
    address: contact.address ?? '',
    addressLine2: contact.addressLine2 ?? '',
    city: contact.city ?? '',
    state: contact.state ?? '',
    zip: contact.zipCode ?? '',
  });
  const needsAddress = addressMattersForFiling && addressIsIncomplete(contact);

  return (
    <div data-testid="selected-contact-address-editor" className="mt-1 space-y-1">
      <a
        href={`/admin/finance/contacts/${contact.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary underline"
      >
        View contact card
        <ExternalLink className="h-3 w-3" />
      </a>

      {needsAddress && !editing && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <p>
            This {noun} needs a mailing address to be itemized on the report.
          </p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-1 font-medium text-primary underline"
          >
            Add address
          </button>
        </div>
      )}

      {editing && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-2">
          <AddressAutocomplete
            value={fields}
            onChange={setFields}
            disabled={isSaving}
            idPrefix="selected-contact"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                await onSaveAddress(fields);
                setEditing(false);
              }}
              disabled={isSaving}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium',
                isSaving
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90',
              )}
            >
              Save address
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
